import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';

import { DiagnosticStatus } from './DiagnosticCard/DiagnosticCounter';
import { InterwillDiagnosticCard } from './DiagnosticCard';

const getStatus = (count: number) => {
  if (count === 0) {
    return DiagnosticStatus.NOT_READY;
  }
  if (count < 3) {
    return DiagnosticStatus.HALF_READY;
  }
  return DiagnosticStatus.READY;
};

type InterwillBeneficiaryDiagnosticCardProps = {
  onCreatePress: () => void;
};

export const InterwillBeneficiaryDiagnosticCard: FC<InterwillBeneficiaryDiagnosticCardProps> = ({
  onCreatePress,
}) => {
  const { t } = useTranslation('pages/pageInterwill', {
    keyPrefix: 'diagnosticCards.participant',
  });

  const { collections } = useSJDatabase();

  const count = useMemo(() => collections.beneficiaries?.size() ?? 0, [collections.beneficiaries]);

  const status = getStatus(count);

  return (
    <InterwillDiagnosticCard
      buttonText={t('buttonText')}
      content={<Content status={status} />}
      count={count}
      iconName="users-three"
      status={status}
      title={t('title')}
      onBtnClick={onCreatePress}
    />
  );
};

const Content: FC<{ status: DiagnosticStatus }> = ({ status }) => {
  const { t } = useTranslation('pages/pageInterwill', {
    keyPrefix: 'diagnosticCards.participant.content',
  });

  const getText = () => {
    if (status === DiagnosticStatus.NOT_READY) {
      return t('primaryText.notReady');
    }
    if (status === DiagnosticStatus.HALF_READY) {
      return t('primaryText.halfReady');
    }
    if (status === DiagnosticStatus.READY) {
      return t('primaryText.ready');
    }

    return '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <span>{getText()}</span>

      {status !== DiagnosticStatus.READY && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span>
            &#x2022; {t('secondaryText.atLeast')} <b>{t('secondaryText.beneficiary')}</b>
          </span>
          <span>
            &#x2022; {t('secondaryText.atLeast')} <b>{t('secondaryText.witnesses')}</b>{' '}
            {t('secondaryText.recommended')}
          </span>
        </div>
      )}
    </div>
  );
};
